import {
  FluentProvider,
  Spinner,
  tokens,
  Toaster,
  useToastController,
} from "@fluentui/react-components";
import { Navigate, Route, Routes  } from "react-router-dom";
import Timesheet from "./Timesheet";
import {useGlobalStatus, GlobalStatusProvider} from '../../services/GlobalStatusProvider';
import { useTeams } from '@microsoft/teamsfx-react';
import {useState, useEffect}  from "react";
import {GetA23WeekTimeEntries, GetTimesheetData, obtainAuthToken} from '../../services/apiService';
import {setTimesheetEntryStatus, updateTimeSheetData, getLockedMonths} from '../../services/TimesheetDataService';
import {getDay, previousMonday, startOfDay} from "date-fns";
import { showErrorToast, TOASTER_ID } from "../../services/toastUtils";
import {jwtDecode} from 'jwt-decode';

export default function TimesheetApp() {
  const [timesheetData, setTimesheetData]= useState([]);
  const [prevWeekTimesheetData, setPrevWeekTimesheetData]= useState([]);
  const [leaveData, setLeaveData] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  const [isTimesheetSubmitted, setIsTimesheetSubmitted] = useState(false);
  const [isAddTimecodeDisabled, setIsAddTimecodeDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timesheetStatusMessage, setTimesheetStatusMessage] = useState("");
  const [timesheetStatus, setTimesheetStatus] = useState(false);
  const { dispatchToast } = useToastController(TOASTER_ID);
  const [isCasualSubmitLocked, setIsCasualSubmitLocked] = useState(false);
  const [lockedMonths, setLockedMonths] = useState(new Set())
  const getEntries = async () => {
    setLoading(true);

    var today = Date();
    const currentWeekStart = getDay(today) > 1 ? previousMonday(startOfDay(today)) : startOfDay(today);
    const lastWeekStart = previousMonday(currentWeekStart);
    let existingResources = [];
    let prevExistingResources =[];

    try {
      existingResources = await GetA23WeekTimeEntries(currentWeekStart);
      prevExistingResources = await GetA23WeekTimeEntries(previousMonday(currentWeekStart));

    } catch {
      showErrorToast(dispatchToast, "Failed to get time entries");
    }

    const currentLockedMonths = getLockedMonths(existingResources);

    const { 
      updatedData: updatedTimesheetData, 
      isLocked: casualSubmitLocked 
    } = await updateTimeSheetData(existingResources, currentWeekStart, currentLockedMonths);

    const { 
      updatedData: updatedLastWeekTimesheetData, 
      isLocked: lastWeekLocked 
    } = await updateTimeSheetData(prevExistingResources, lastWeekStart, currentLockedMonths);
    
    // Need to do it this way otherwise it will stay set to true when cycling weeks if it has been set
    if (casualSubmitLocked) {
        setIsCasualSubmitLocked(true);
    } else {
        setIsCasualSubmitLocked(false);
    }

    // Get timesheet status
    let timesheet = '';
    try {
      timesheet = await GetTimesheetData(currentWeekStart);
    } catch {
      showErrorToast(dispatchToast, "Failed to get timesheet status");
    }

    if (timesheet.timesheetStatus === "Submitted") {
        updatedTimesheetData.forEach(client => {
            client.projects.forEach(project => {
                project.budgets.forEach(budget => {
                    budget.timecodes.forEach(timecode => {
                        timecode.entryDays.forEach(day => {
                            day.status = "closed";
                        });
                    });
                });
            });
        });
        setIsAddTimecodeDisabled(true);
        setIsTimesheetSubmitted(true);
    }

    setLeaveData(timesheet.leaveDays);
    setCalendarData(timesheet.holidayCalendars);
    setLockedMonths(currentLockedMonths);

    switch (timesheet.timesheetStatus) {
      case "Submitted":
        setTimesheetEntryStatus(updatedTimesheetData, "closed");
        setTimesheetStatusMessage("Timesheet Submitted");
        setIsAddTimecodeDisabled(true);
        setIsTimesheetSubmitted(true);
        setTimesheetStatus(false);
        break;
      case "Needs Attention":
        setTimesheetEntryStatus(updatedTimesheetData, "rejected");
        setTimesheetStatusMessage("Timesheet Rejected!");
        setTimesheetStatus(true);
        break;
      case "Approved":
        setTimesheetEntryStatus(updatedTimesheetData, "approved");
        setTimesheetStatusMessage("Timesheet Approved :)");
        setIsAddTimecodeDisabled(true);
        setIsTimesheetSubmitted(true);
        setTimesheetStatus(false);
        break;
      default:
        setTimesheetStatusMessage("");
        setTimesheetStatus(true);
      }

    setTimesheetData(updatedTimesheetData);
    setPrevWeekTimesheetData(updatedLastWeekTimesheetData);

    // Check admin 
    const checkAdmin = async () => {
    var token = await obtainAuthToken();

    // Check if user is allowed to use admin panel (part of security group)
    const decoded = jwtDecode(token);
    const groups = decoded.groups || [];

    const adminGroup = process.env.REACT_APP_ADMIN_GROUP;
    if (groups.includes(adminGroup)) {
      setIsAdmin(true);
    }
      
    };

    if (process.env.REACT_APP_ENVIRONMENT === 'local') {
      setIsAdmin(true);
    } else {
      checkAdmin();
    }

    setLoading(false);
  }

  // App initialisation
  useEffect(() => {
    getEntries();
  }, []);

  
  return (
    <GlobalStatusProvider loading={loading} setLoading={setLoading} isAddTimecodeDisabled={isAddTimecodeDisabled} setIsAddTimecodeDisabled={setIsAddTimecodeDisabled} 
    isTimesheetSubmitted={isTimesheetSubmitted} setIsTimesheetSubmitted={setIsTimesheetSubmitted} timesheetStatus={timesheetStatus} setTimesheetStatus={setTimesheetStatus}
    isCasualSubmitLocked={isCasualSubmitLocked} setIsCasualSubmitLocked={setIsCasualSubmitLocked} lockedMonths={lockedMonths} setLockedMonths={setLockedMonths}
>
      <Toaster toasterId={TOASTER_ID} />
      <AppContent isAdmin={isAdmin} timesheetData={timesheetData} prevWeekTimesheetData={prevWeekTimesheetData} leaveData={leaveData} setLeaveData={setLeaveData} setTimesheetData={setTimesheetData} setPrevWeekTimesheetData={setPrevWeekTimesheetData} timesheetStatusMessage={timesheetStatusMessage} setTimesheetStatusMessage={setTimesheetStatusMessage} setCalendarData={setCalendarData} calendarData={calendarData}/>
    </GlobalStatusProvider>
  );
}



function AppContent(props) {
  const [{theme}] = useTeams({});
  const{loading}= useGlobalStatus();

  return (
    <FluentProvider
    theme={theme}
    style={{ background: tokens.colorNeutralBackground3 }}>     
    <div style={{padding:20}}>
      {loading ? (
        <div style={{position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          width: '100vw',
          backgroundColor: theme.colorNeutralBackground1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.85,
          zIndex: 9999,}}>
          <Spinner />
        </div>
        ) : (
          <></>
        )
      }
      <Routes>              
        <Route path="/timesheet" element={<Timesheet isAdmin={props.isAdmin} timesheetData={props.timesheetData} prevWeekTimesheetData={props.prevWeekTimesheetData} leaveData={props.leaveData} setLeaveData={props.setLeaveData} setTimesheetData={props.setTimesheetData} setPrevWeekTimesheetData={props.setPrevWeekTimesheetData} timesheetStatusMessage={props.timesheetStatusMessage} setTimesheetStatusMessage={props.setTimesheetStatusMessage}  setCalendarData={props.setCalendarData} calendarData={props.calendarData}/>} />
        <Route path="*" element={<Navigate to={"/timesheet"} />}></Route>
      </Routes>      
    </div> 
  </FluentProvider>
  );
}
